/**
 * CampaignColumnChooser — first-touch attribution "Choose report columns" dialog.
 * Lets users select which columns to display in the campaign table,
 * including custom user-defined metrics.
 */
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { X, Plus, GripVertical, Pencil, Search, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { CustomDashboardColumn } from "@shared/schema";
import { DEFAULT_CAMPAIGN_COLUMNS } from "@shared/schema";

interface CampaignColumnChooserProps {
  columns: CustomDashboardColumn[];
  onChange: (columns: CustomDashboardColumn[]) => void;
  onClose: () => void;
}

const CATEGORY_LABEL_KEYS: Record<string, string> = {
  general: "columnChooser.category.general",
  calls: "columnChooser.category.calls",
  lead_gen: "columnChooser.category.lead_gen",
  ecommerce: "columnChooser.category.ecommerce",
  subscriptions: "columnChooser.category.subscriptions",
  custom: "columnChooser.category.custom",
};

const CATEGORY_ORDER = ["general", "calls", "lead_gen", "ecommerce", "subscriptions", "custom"];

const FORMAT_LABEL_KEYS: Record<string, string> = {
  currency: "columnChooser.format.currency",
  number: "columnChooser.format.number",
  percent: "columnChooser.format.percent",
  ratio: "columnChooser.format.ratio",
};

export default function CampaignColumnChooser({
  columns,
  onChange,
  onClose,
}: CampaignColumnChooserProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("general");
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customLabel, setCustomLabel] = useState("");
  const [customFormat, setCustomFormat] = useState<"currency" | "number" | "percent" | "ratio">(
    "number"
  );
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");

  const selectedColumns = columns.filter((c) => c.visible).sort((a, b) => a.order - b.order);

  const categorizedColumns = useMemo(() => {
    const grouped = new Map<string, CustomDashboardColumn[]>();
    for (const cat of CATEGORY_ORDER) {
      grouped.set(cat, []);
    }
    for (const col of columns) {
      const list = grouped.get(col.category) || [];
      list.push(col);
      grouped.set(col.category, list);
    }
    return grouped;
  }, [columns]);

  const availableInCategory = useMemo(() => {
    const catCols = categorizedColumns.get(activeCategory) || [];
    if (!searchQuery) return catCols.filter((c) => !c.visible);
    return catCols.filter(
      (c) => !c.visible && c.label.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [categorizedColumns, activeCategory, searchQuery]);

  const toggleColumn = (key: string) => {
    const updated = columns.map((c) =>
      c.key === key
        ? { ...c, visible: !c.visible, order: c.visible ? c.order : selectedColumns.length }
        : c
    );
    onChange(updated);
  };

  const removeColumn = (key: string) => {
    const updated = columns.map((c) => (c.key === key ? { ...c, visible: false } : c));
    onChange(updated);
  };

  const moveColumn = (key: string, direction: "up" | "down") => {
    const selected = columns.filter((c) => c.visible).sort((a, b) => a.order - b.order);
    const idx = selected.findIndex((c) => c.key === key);
    if (idx < 0) return;

    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= selected.length) return;

    const orderMap = new Map<string, number>();
    selected.forEach((c, i) => orderMap.set(c.key, i));
    const currentOrder = orderMap.get(key)!;
    const swapOrder = orderMap.get(selected[swapIdx].key)!;
    orderMap.set(key, swapOrder);
    orderMap.set(selected[swapIdx].key, currentOrder);

    const updated = columns.map((c) => ({
      ...c,
      order: orderMap.has(c.key) ? orderMap.get(c.key)! : c.order,
    }));
    onChange(updated);
  };

  const addCustomColumn = () => {
    if (!customLabel.trim()) return;
    const key = `custom_${Date.now()}`;
    const newCol: CustomDashboardColumn = {
      key,
      label: customLabel.trim(),
      category: "custom",
      isCustom: true,
      visible: true,
      order: selectedColumns.length,
      format: customFormat,
    };
    onChange([...columns, newCol]);
    setCustomLabel("");
    setShowCustomForm(false);
  };

  const deleteCustomColumn = (key: string) => {
    onChange(columns.filter((c) => c.key !== key));
  };

  const startEdit = (col: CustomDashboardColumn) => {
    setEditingKey(col.key);
    setEditLabel(col.label);
  };

  const saveEdit = () => {
    if (!editingKey || !editLabel.trim()) return;
    const updated = columns.map((c) =>
      c.key === editingKey ? { ...c, label: editLabel.trim() } : c
    );
    onChange(updated);
    setEditingKey(null);
    setEditLabel("");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-[720px] max-h-[80vh] bg-[#0a0c10] border border-white/[0.12] rounded-xl shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.08]">
          <h2 className="text-sm font-medium text-white">{t("columnChooser.title")}</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white/60 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Left: Categories */}
          <div className="w-[180px] border-r border-white/[0.08] py-2 overflow-y-auto flex-shrink-0">
            {CATEGORY_ORDER.map((cat) => {
              const count = (categorizedColumns.get(cat) || []).length;
              if (count === 0 && cat !== "custom") return null;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`w-full flex items-center justify-between px-4 py-2 text-xs transition-colors ${
                    activeCategory === cat
                      ? "bg-emerald-600/15 text-emerald-400 border-r-2 border-emerald-400"
                      : "text-white/60 hover:text-white hover:bg-white/[0.04]"
                  }`}
                >
                  <span>{CATEGORY_LABEL_KEYS[cat] ? t(CATEGORY_LABEL_KEYS[cat]) : cat}</span>
                  {count > 0 && <span className="text-[10px] text-white/30">{count}</span>}
                </button>
              );
            })}
          </div>

          {/* Center: Available columns */}
          <div className="flex-1 border-r border-white/[0.08] py-3 px-4 overflow-y-auto min-w-0">
            <div className="flex items-center gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("columnChooser.search")}
                  className="w-full h-8 pl-8 pr-2 text-xs bg-[#030407] border border-white/[0.1] rounded-md text-white placeholder:text-white/20"
                />
              </div>
              {activeCategory === "custom" && (
                <Button
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white text-xs h-8"
                  onClick={() => setShowCustomForm(true)}
                >
                  <Plus className="w-3 h-3 mr-1" /> {t("columnChooser.create")}
                </Button>
              )}
            </div>

            {/* Custom column creation */}
            {showCustomForm && (
              <div className="mb-3 p-3 bg-white/[0.03] border border-white/[0.1] rounded-lg space-y-2">
                <input
                  type="text"
                  value={customLabel}
                  onChange={(e) => setCustomLabel(e.target.value)}
                  placeholder={t("columnChooser.metricName")}
                  className="w-full h-8 px-2 text-xs bg-[#030407] border border-white/[0.1] rounded-md text-white placeholder:text-white/20"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") addCustomColumn();
                  }}
                />
                <select
                  value={customFormat}
                  onChange={(e) => setCustomFormat(e.target.value as typeof customFormat)}
                  className="w-full h-8 px-2 text-xs bg-[#030407] border border-white/[0.1] rounded-md text-white"
                >
                  {Object.entries(FORMAT_LABEL_KEYS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {t(v)}
                    </option>
                  ))}
                </select>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs flex-1"
                    onClick={addCustomColumn}
                  >
                    {t("columnChooser.add")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-xs text-white/50"
                    onClick={() => setShowCustomForm(false)}
                  >
                    {t("columnChooser.cancel")}
                  </Button>
                </div>
              </div>
            )}

            {/* Select All */}
            <label className="flex items-center gap-2 mb-2 cursor-pointer">
              <input
                type="checkbox"
                checked={
                  availableInCategory.length === 0 &&
                  (categorizedColumns.get(activeCategory) || []).length > 0
                }
                onChange={() => {
                  const catCols = categorizedColumns.get(activeCategory) || [];
                  const allVisible = catCols.every((c) => c.visible);
                  const updated = columns.map((c) =>
                    c.category === activeCategory ? { ...c, visible: !allVisible } : c
                  );
                  onChange(updated);
                }}
                className="accent-red-500"
              />
              <span className="text-xs text-white/60">{t("columnChooser.selectAll")}</span>
            </label>

            {/* Column checkboxes */}
            <div className="space-y-1">
              {(categorizedColumns.get(activeCategory) || []).map((col) => (
                <label
                  key={col.key}
                  className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-white/[0.04] cursor-pointer group"
                >
                  <input
                    type="checkbox"
                    checked={col.visible}
                    onChange={() => toggleColumn(col.key)}
                    className="accent-red-500"
                  />
                  <span className="text-xs text-white/70 flex-1">{col.label}</span>
                  {col.isCustom && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        deleteCustomColumn(col.key);
                      }}
                      className="opacity-0 group-hover:opacity-100 text-white/30 hover:text-red-400 transition-all"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </label>
              ))}
            </div>
          </div>

          {/* Right: Selected columns */}
          <div className="w-[220px] py-3 px-3 overflow-y-auto flex-shrink-0">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-white/40 mb-2">
              {t("columnChooser.selectedCount", { count: selectedColumns.length })}
            </p>

            <div className="space-y-1">
              {selectedColumns.map((col) => (
                <div
                  key={col.key}
                  className="flex items-center gap-1.5 py-1.5 px-2 bg-white/[0.03] border border-white/[0.08] rounded group"
                >
                  <GripVertical className="w-3 h-3 text-white/20 flex-shrink-0 cursor-grab" />
                  {editingKey === col.key ? (
                    <input
                      type="text"
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      onBlur={saveEdit}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveEdit();
                        if (e.key === "Escape") setEditingKey(null);
                      }}
                      className="flex-1 h-6 px-1 text-xs bg-[#030407] border border-white/[0.1] rounded text-white"
                      autoFocus
                    />
                  ) : (
                    <span className="text-xs text-white/70 flex-1 truncate">{col.label}</span>
                  )}
                  <button
                    onClick={() => startEdit(col)}
                    className="opacity-0 group-hover:opacity-100 text-white/30 hover:text-white/60 transition-all"
                    title={t("columnChooser.rename")}
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => removeColumn(col.key)}
                    className="text-white/30 hover:text-red-400 transition-colors"
                    title={t("columnChooser.removeFromView")}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/[0.08]">
          <Button size="sm" variant="ghost" className="text-xs text-white/50" onClick={onClose}>
            {t("columnChooser.cancel")}
          </Button>
          <Button
            size="sm"
            className="bg-red-600 hover:bg-red-700 text-white text-xs"
            onClick={onClose}
          >
            {t("columnChooser.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}
